import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import type { RootState } from '@/rootReducer';
import './AddBccModal.scss';
import {
	userToggleAddBccModal,
	userAddNewBccRequest,
	userAddNewBccSuccessOff,
	userDeleteBccRequest
} from '@/modules/user/userActions';
import { DropDownGold, IconWarningOutline, Checkmark } from '@/assets/images';
import type { User } from '../userTypes';

type Props = {
	user: User;
};

export default function AddBccModal({}: Props): JSX.Element {
	const [addBccModalOpen, addBccLoading, addBccSuccess, addBccFailure, user] =
		useSelector<RootState, [any, any, any, any, any]>(
			(state: RootState) => [
				state.user.getIn(['authorize', 'addBccModalOpen']),
				state.user.getIn(['addBcc', 'loading']),
				state.user.getIn(['addBcc', 'success']),
				state.user.getIn(['addBcc', 'failure']),
				state.user.getIn(['authorize', 'user']).toJS()
			],
			shallowEqual
		);

	useEffect(() => {
		if (addBccModalOpen === true) {
			setSuccessOpen(false);
			setActionType('');
			setCrm('');
			setActionType(false);
			setErrors([]);
			setDeleteMode(false);
			if (user && user.crmBcc && Object.keys(user.crmBcc).length > 0) {
				setCrmBcc();
			}
		}
	}, [addBccModalOpen]);

	const [successOpen, setSuccessOpen] = useState(false);
	const [successTimeout, setSuccessTimeout] = useState();

	const timeClose = () => {
		closeAddBccModal();
	};

	const [loading, setLoading] = useState(false);
	useEffect(() => {
		if (addBccLoading === true) {
			setLoading(true);
		}
	}, [addBccLoading]);

	useEffect(() => {
		if (addBccSuccess === true) {
			setTimeout(() => {
				setLoading(false);
				setSuccessOpen(true);
				setTimeout(() => {
					const el =
						document.getElementsByClassName('success-toast')[0];
					if (el) {
						el.classList.add('toast-visible');
					}
				}, 15);
				setTimeout(() => {
					const el =
						document.getElementsByClassName('success-toast')[0];
					if (el) {
						el.classList.remove('toast-visible');
					}
				}, 4700);
				setSuccessTimeout(
					setTimeout(() => {
						timeClose();
					}, 5000)
				);
			}, 3000);
		}
	}, [addBccSuccess]);

	useEffect(() => {
		if (addBccFailure === true) {
			alert(
				'Sorry, there was a problem with this Bcc Email action. Please try again or contact support.'
			);
			setLoading(false);
			closeAddBccModal();
		}
	}, [addBccFailure]);

	const setCrmBcc = () => {
		setTimeout(() => {
			document.getElementById('add-bcc-modal-crm-email').value =
				user.crmBcc.email;
			if (user.crmBcc.initialEmails) {
				document
					.getElementById('initial-email')
					.classList.add('selected');
			}
			if (user.crmBcc.replyEmails) {
				document
					.getElementById('reply-email')
					.classList.add('selected');
			}
			if (user.crmBcc.crmType) {
				setCrm(user.crmBcc.crmType);
			}
			setOtherCrm(false);
		}, 250);
	};

	const dispatch = useDispatch();

	const addNewBccRequest = payload => dispatch(userAddNewBccRequest(payload));

	const deleteBccRequest = () => dispatch(userDeleteBccRequest());

	const resetModal = () => dispatch(userAddNewBccSuccessOff());

	const [actionType, setActionType] = useState('');

	const closeAddBccModal = () => {
		dispatch(userToggleAddBccModal(false));
	};

	const openAddBccModal = () => {
		resetModal();
		clearTimeout(successTimeout);
		setSuccessOpen(false);
		setActionType('');
		setCrm('');
		setActionType(false);
		setErrors([]);
		setDeleteMode(false);
		if (user && user.crmBcc && Object.keys(user.crmBcc).length > 0) {
			setCrmBcc();
		}
	};

	const toggleDropdown = evt => {
		if (evt.currentTarget.classList.contains('opened')) {
			evt.currentTarget.classList.remove('opened');
		} else {
			evt.currentTarget.classList.add('opened');
		}
	};

	const closeDropdown = e => {
		e.stopPropagation();
		const el = document.getElementsByClassName(
			'add-bcc-modal-crm-dropdown'
		)[0];
		if (el) {
			el.classList.remove('opened');
		}
	};

	let [crm, setCrm] = useState('');

	const selectCrm = type => {
		setCrm(type);
		if (type === 'Other') {
			setOtherCrm(true);
		} else {
			setOtherCrm(false);
		}
	};

	const toggleSwitch = e => {
		if (e.currentTarget.classList.contains('selected')) {
			e.currentTarget.classList.remove('selected');
		} else {
			e.currentTarget.classList.add('selected');
		}
	};

	const [errors, setErrors] = useState([]);

	const [deleteMode, setDeleteMode] = useState(false);
	const [otherCrm, setOtherCrm] = useState(false);

	const saveBcc = () => {
		const bccObj = {};
		const errors = [];
		if (crm && crm !== 'Other') {
			bccObj.crm = crm;
		} else if (crm && crm === 'Other') {
			const el = document.getElementById('add-bcc-modal-crm-other');
			bccObj.crm = el.value;
		} else {
			bccObj.crm = '';
		}
		const emailEl = document.getElementById('add-bcc-modal-crm-email');
		const emailRegExp =
			/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;
		if (
			emailEl.value &&
			emailEl.value.length > 0 &&
			emailRegExp.test(emailEl.value)
		) {
			bccObj.email = emailEl.value;
		} else if (
			emailEl.value &&
			emailEl.value.length > 0 &&
			!emailRegExp.test(emailEl.value)
		) {
			errors.push('emailRegex');
		} else {
			errors.push('email');
		}
		const initialEmailEl = document.getElementById('initial-email');
		if (initialEmailEl.classList.contains('selected')) {
			bccObj.initialEmail = true;
		}
		const replyEmailEl = document.getElementById('reply-email');
		if (replyEmailEl.classList.contains('selected')) {
			bccObj.replyEmail = true;
		}
		if (!bccObj.initialEmail && !bccObj.replyEmail) {
			errors.push('emailType');
		}
		if (errors.length === 0) {
			setErrors([]);
			addNewBccRequest(bccObj);
			if (user?.crmBcc && Object.keys(user?.crmBcc).length > 0) {
				setActionType('save');
			} else {
				setActionType('add');
			}
		} else {
			setErrors(errors);
			const el = document.getElementsByClassName(
				'add-bcc-modal-scrollable-container'
			)[0];
			el.scrollTop = 1000;
			setTimeout(() => {
				el.scrollTop = 1500;
			}, 250);
		}
	};

	const deleteBcc = () => {
		deleteBccRequest();
		setActionType('delete');
		setDeleteMode(false);
	};

	return (
		<>
			{addBccModalOpen && !deleteMode && !successOpen && (
				<>
					<div className="add-bcc-modal-outer">
						<div
							className="add-bcc-modal-bg"
							onClick={closeAddBccModal}
						/>
						<div
							className={`add-bcc-modal-inner ${loading ? 'loading-inner' : ''}`}
						>
							{loading ? (
								<>
									<div className="add-bcc-modal-loading-title">
										Saving CRM BCC Settings
									</div>
									<div className="add-bcc-modal-loading-ball-holder">
										<div className="add-bcc-modal-loading-ball add-bcc-modal-loading-ball-1" />
										<div className="add-bcc-modal-loading-ball add-bcc-modal-loading-ball-2" />
										<div className="add-bcc-modal-loading-ball add-bcc-modal-loading-ball-3" />
										<div className="add-bcc-modal-loading-ball add-bcc-modal-loading-ball-4" />
									</div>
								</>
							) : (
								<>
									<div className="add-bcc-modal-scrollable-container">
										<div className="add-bcc-modal-title">
											Add CRM BCC
										</div>
										<div className="add-bcc-modal-subtitle">
											Keep your CRM automatically synced
											with your activity on Resquared
										</div>
										<div className="add-bcc-modal-input-title">
											CRM
										</div>
										<div
											className="add-bcc-modal-crm-dropdown"
											onClick={evt => {
												toggleDropdown(evt);
												evt.currentTarget.classList.add(
													'input-focused'
												);
											}}
										>
											<div className="add-bcc-modal-crm-dropdown-title">
												Select Your CRM System
												<DropDownGold className="add-bcc-modal-crm-dropdown-chevron" />
											</div>
											<input
												value={crm}
												onChange={() => {}}
												className="add-bcc-modal-input crm-input"
												placeholder="Select Your CRM"
												onFocus={e => {
													e.currentTarget.blur();
												}}
											/>
											<div
												className="add-bcc-modal-crm-dropdown-bg"
												onClick={closeDropdown}
											/>
											<div className="add-bcc-modal-crm-dropdown-select">
												<div
													className="add-bcc-modal-crm-dropdown-option"
													onClick={() => {
														selectCrm('');
													}}
												>
													Select Your CRM
												</div>
												<div
													className={`add-bcc-modal-crm-dropdown-option ${crm === 'Hubspot' ? 'selected-option' : ''}`}
													onClick={() => {
														selectCrm('Hubspot');
													}}
												>
													Hubspot
												</div>
												<div
													className={`add-bcc-modal-crm-dropdown-option ${crm === 'Salesforce' ? 'selected-option' : ''}`}
													onClick={() => {
														selectCrm('Salesforce');
													}}
												>
													Salesforce
												</div>
												<div
													className={`add-bcc-modal-crm-dropdown-option ${crm === 'Basecamp' ? 'selected-option' : ''}`}
													onClick={() => {
														selectCrm('Basecamp');
													}}
												>
													Basecamp
												</div>
												<div
													className={`add-bcc-modal-crm-dropdown-option ${crm === 'Other' ? 'selected-option' : ''}`}
													onClick={() => {
														selectCrm('Other');
													}}
												>
													Other
												</div>
											</div>
										</div>
										{otherCrm && (
											<>
												<div className="add-bcc-modal-input-title">
													CRM Name
												</div>
												<div className="add-bcc-modal-input-holder">
													<div className="add-bcc-modal-crm-dropdown-title">
														Type in the name of your
														CRM
													</div>
													<input
														id="add-bcc-modal-crm-other"
														className="add-bcc-modal-input"
														placeholder="Enter CRM name"
														onFocus={evt => {
															evt.currentTarget.parentNode.classList.add(
																'input-focused'
															);
														}}
														onBlur={evt => {
															evt.currentTarget.parentNode.classList.remove(
																'input-focused'
															);
														}}
													/>
												</div>
											</>
										)}
										<div className="add-bcc-modal-input-title">
											CRM Email Name
										</div>
										<div className="add-bcc-modal-input-holder">
											<div className="add-bcc-modal-crm-dropdown-title">
												Enter the email to Bcc on
												Resquared communications
											</div>
											<input
												id="add-bcc-modal-crm-email"
												className="add-bcc-modal-input"
												placeholder="Enter CRM Email Address"
												autoComplete="new-password"
												onFocus={evt => {
													evt.currentTarget.parentNode.classList.add(
														'input-focused'
													);
												}}
												onBlur={evt => {
													evt.currentTarget.parentNode.classList.remove(
														'input-focused'
													);
												}}
												onKeyUp={() => {
													let errorSet = [...errors];
													errorSet = errorSet.filter(
														err => {
															return (
																err !== 'email'
															);
														}
													);
													setErrors(errorSet);
												}}
											/>
											{errors.includes('email') && (
												<div className="add-bcc-modal-required">
													<IconWarningOutline className="add-bcc-modal-required-warning" />{' '}
													Enter a CRM Email Address
													before saving
												</div>
											)}
											{errors.includes('emailRegex') && (
												<div className="add-bcc-modal-required">
													<IconWarningOutline className="add-bcc-modal-required-warning" />{' '}
													Enter a properly-formatted
													CRM Email Address before
													saving
												</div>
											)}
										</div>
										<div className="add-bcc-modal-toggles">
											<div className="add-bcc-modal-toggle-holder">
												<div className="add-bcc-modal-toggle-text">
													Bcc for all initial emails
												</div>
												<div
													id="initial-email"
													className={`add-bcc-modal-toggle-switch-outer ${user?.crmBcc && Object.keys(user?.crmBcc).length === 0 ? 'selected' : ''}`}
													onClick={e => {
														toggleSwitch(e);
														let errorSet = [
															...errors
														];
														errorSet =
															errorSet.filter(
																err => {
																	return (
																		err !==
																		'emailType'
																	);
																}
															);
														setErrors(errorSet);
													}}
												>
													<div className="add-bcc-modal-toggle-switch-inner" />
												</div>
											</div>
											<div className="add-bcc-modal-toggle-holder">
												<div className="add-bcc-modal-toggle-text">
													Bcc for all reply emails
												</div>
												<div
													id="reply-email"
													className="add-bcc-modal-toggle-switch-outer"
													onClick={toggleSwitch}
												>
													<div className="add-bcc-modal-toggle-switch-inner" />
												</div>
											</div>
											{errors.includes('emailType') && (
												<div className="add-bcc-modal-required email-type-required">
													<IconWarningOutline className="add-bcc-modal-required-warning" />{' '}
													Turn on at least one CRM Bcc
													setting before saving
												</div>
											)}
										</div>
									</div>
									<div className="add-bcc-modal-buttons-holder">
										{user &&
											user.crmBcc &&
											Object.keys(user.crmBcc).length >
												0 && (
												<div
													className="add-bcc-modal-delete-button"
													onClick={() => {
														setDeleteMode(true);
													}}
												>
													Remove CRM Bcc
												</div>
											)}

										<div
											className="add-bcc-modal-cancel-button"
											onClick={closeAddBccModal}
										>
											Cancel
										</div>
										<div
											className="add-bcc-modal-save-button"
											onClick={saveBcc}
										>
											Save
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</>
			)}
			{addBccModalOpen && deleteMode && !successOpen && (
				<>
					<div className="add-bcc-modal-outer">
						<div
							className="add-bcc-modal-bg"
							onClick={closeAddBccModal}
						/>
						<div className="add-bcc-delete-holder">
							<div className="add-bcc-delete-title">
								Are you sure you want to delete your CRM BCC?
							</div>
							<div className="add-bcc-delete-subtitle">
								Keep your CRM automatically synced with your
								activity on Resquared
								<br />
								<br />
								Change this anytime via the Profile Dropdown.
							</div>
							<div className="add-bcc-modal-buttons-holder">
								<div
									className="add-bcc-modal-cancel-button"
									onClick={() => {
										setDeleteMode(false);
									}}
								>
									Cancel
								</div>
								<div
									className="add-bcc-delete-delete-button"
									onClick={deleteBcc}
								>
									Remove CRM BCC
								</div>
							</div>
						</div>
					</div>
				</>
			)}
			{addBccModalOpen && successOpen && actionType === 'save' && (
				<div className="success-toast">
					<Checkmark className="success-toast-check" />
					CRM Bcc settings saved successfully.
					<span onClick={openAddBccModal}>
						&nbsp;&nbsp;Edit Settings
					</span>
				</div>
			)}
			{addBccModalOpen && successOpen && actionType === 'delete' && (
				<div className="success-toast">
					<Checkmark className="success-toast-check" />
					CRM Bcc removed successfully.
					<span onClick={openAddBccModal}>
						&nbsp;&nbsp;Add CRM Bcc back
					</span>
				</div>
			)}
			{addBccModalOpen && successOpen && actionType === 'add' && (
				<div className="success-toast toast-visible">
					<Checkmark className="success-toast-check" />
					CRM Bcc settings saved successfully.
					<span onClick={openAddBccModal}>
						&nbsp;&nbsp;Edit Settings
					</span>
				</div>
			)}
		</>
	);
}
